/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { formatDateForInput } from "@/app/utils/dateUtils";
import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";

interface UnsavedChangesContextType {
  // Core state
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;

  // Form management
  registerForm: (formId: string, initialData: any) => void;
  unregisterForm: (formId: string) => void;
  updateFormData: (formId: string, data: any) => void;
  resetFormData: (formId: string) => void;
  resetAllForms: () => void;
  updateInitialData: (formId: string, newInitialData: any) => void;
  updateBaselineData: (formId: string, newBaselineData: any) => void;

  // Change detection
  checkForChanges: () => boolean;
  getFormChanges: (
    formId: string
  ) => { field: string; oldValue: any; newValue: any }[];
  getAllChanges: () => Record<
    string,
    { field: string; oldValue: any; newValue: any }[]
  >;

  // Navigation protection
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
  pendingNavigation: string | null;
  setPendingNavigation: (navigation: string | null) => void;
  pendingNavigationData: any;
  setPendingNavigationData: (data: any) => void;
  attemptNavigation: (href: string, method: "push" | "replace") => void;

  // Role-based access
  isFormEnabled: (formId: string) => boolean;
  setFormEnabled: (formId: string, enabled: boolean) => void;

  // Section-specific operations
  updateSectionData: (formId: string, section: string, data: any) => void;
  resetSectionData: (formId: string, section: string) => void;
  getSectionChanges: (
    formId: string,
    section: string
  ) => { field: string; oldValue: any; newValue: any }[];
  hasSectionChanges: (formId: string, section: string) => boolean;
  getSectionChangeCount: (formId: string, section: string) => number;
  getAllSectionChanges: (
    formId: string
  ) => Record<string, { field: string; oldValue: any; newValue: any }[]>;
  resetAllSections: (formId: string) => void;
  saveSectionData: (
    formId: string,
    section: string,
    data: any
  ) => Promise<boolean>;

  // Visual indicators
  getUnsavedFieldsCount: () => number;
  hasFormUnsavedChanges: (formId: string) => boolean;
  getChanges: (
    formId: string
  ) => { field: string; oldValue: any; newValue: any }[];

  // Data integrity
  validateFormData: (formId: string) => boolean;
  saveFormData: (formId: string, data: any) => Promise<boolean>;
  discardFormChanges: (formId: string) => void;
}

const UnsavedChangesContext = createContext<
  UnsavedChangesContextType | undefined
>(undefined);

interface UnsavedChangesProviderProps {
  children: React.ReactNode;
}

export function UnsavedChangesProvider({
  children,
}: UnsavedChangesProviderProps) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [pendingNavigationData, setPendingNavigationData] = useState<any>(null);

  // Store form data and initial data for comparison
  const formDataRef = useRef<Record<string, any>>({});
  const initialDataRef = useRef<Record<string, any>>({});
  const sectionDataRef = useRef<Record<string, Record<string, any>>>({});
  const initialSectionDataRef = useRef<Record<string, Record<string, any>>>({});
  const registeredFormsRef = useRef<Set<string>>(new Set());
  const enabledFormsRef = useRef<Set<string>>(new Set());
  const formValidationRef = useRef<Record<string, (data: any) => boolean>>({});
  const formSaveHandlersRef = useRef<
    Record<string, (data: any) => Promise<boolean>>
  >({});

  // Register a form with its initial data
  const registerForm = useCallback(
    (
      formId: string,
      initialData: any,
      options?: {
        enabled?: boolean;
        validator?: (data: any) => boolean;
        saveHandler?: (data: any) => Promise<boolean>;
        sections?: string[];
      }
    ) => {
      try {
        console.log(`[UnsavedChanges] Registering form: ${formId}`, {
          hasInitialData: !!initialData,
          options: options ? Object.keys(options) : "none",
        });

        // Validate inputs
        if (!formId || typeof formId !== "string") {
          throw new Error("Form ID must be a non-empty string");
        }

        if (!initialData || typeof initialData !== "object") {
          console.warn(
            `[UnsavedChanges] Form ${formId} registered with invalid initial data`
          );
          initialData = {};
        }

        // Store form data
        initialDataRef.current[formId] = { ...initialData };
        formDataRef.current[formId] = { ...initialData };
        registeredFormsRef.current.add(formId);

        // Set enabled state (default: true)
        if (options?.enabled !== false) {
          enabledFormsRef.current.add(formId);
        }

        // Set validation and save handlers
        if (options?.validator) {
          formValidationRef.current[formId] = options.validator;
        }
        if (options?.saveHandler) {
          formSaveHandlersRef.current[formId] = options.saveHandler;
        }

        // Initialize section data if sections are provided
        if (options?.sections && options.sections.length > 0) {
          if (!sectionDataRef.current[formId]) {
            sectionDataRef.current[formId] = {};
          }
          if (!initialSectionDataRef.current[formId]) {
            initialSectionDataRef.current[formId] = {};
          }

          // Initialize each section with empty data
          options.sections.forEach((section) => {
            sectionDataRef.current[formId][section] = {};
            initialSectionDataRef.current[formId][section] = {};
          });
        }

        // Check for changes after registration
        checkForChanges();
      } catch (error) {
        console.error(
          `[UnsavedChanges] Failed to register form ${formId}:`,
          error
        );
      }
    },
    []
  );

  // Unregister a form
  const unregisterForm = useCallback((formId: string) => {
    try {
      console.log(`[UnsavedChanges] Unregistering form: ${formId}`);

      // Clean up all form data
      delete initialDataRef.current[formId];
      delete formDataRef.current[formId];
      delete sectionDataRef.current[formId];
      delete initialSectionDataRef.current[formId];
      delete formValidationRef.current[formId];
      delete formSaveHandlersRef.current[formId];

      // Remove from registered and enabled sets
      registeredFormsRef.current.delete(formId);
      enabledFormsRef.current.delete(formId);

      // Check for changes after unregistration
      checkForChanges();
    } catch (error) {
      console.error(
        `[UnsavedChanges] Failed to unregister form ${formId}:`,
        error
      );
    }
  }, []);

  // Update form data
  const updateFormData = useCallback((formId: string, data: any) => {
    try {
      if (!registeredFormsRef.current.has(formId)) {
        console.warn(
          `[UnsavedChanges] Attempted to update unregistered form: ${formId}`
        );
        return;
      }

      if (!enabledFormsRef.current.has(formId)) {
        console.log(
          `[UnsavedChanges] Form ${formId} is disabled, skipping update`
        );
        return;
      }

      if (!data || typeof data !== "object") {
        console.warn(
          `[UnsavedChanges] Invalid data provided for form ${formId}`
        );
        return;
      }

      console.log(`[UnsavedChanges] Updating form data: ${formId}`, {
        fieldsCount: Object.keys(data).length,
        hasChanges:
          JSON.stringify(formDataRef.current[formId]) !== JSON.stringify(data),
      });

      formDataRef.current[formId] = { ...data };

      // Check for changes after update
      checkForChanges();
    } catch (error) {
      console.error(
        `[UnsavedChanges] Failed to update form data for ${formId}:`,
        error
      );
    }
  }, []);

  // Reset form data to initial state
  const resetFormData = useCallback((formId: string) => {
    if (
      registeredFormsRef.current.has(formId) &&
      initialDataRef.current[formId]
    ) {
      console.log(`[UnsavedChanges] Resetting form data: ${formId}`);
      formDataRef.current[formId] = { ...initialDataRef.current[formId] };

      // Check for changes after reset
      checkForChanges();
    }
  }, []);

  // Update initial data for a form (used when data is refreshed from server)
  const updateInitialData = useCallback(
    (formId: string, newInitialData: any) => {
      if (registeredFormsRef.current.has(formId)) {
        console.log(
          `[UnsavedChanges] Updating initial data for form: ${formId}`
        );
        initialDataRef.current[formId] = { ...newInitialData };

        // Also update current form data to match the new initial data
        formDataRef.current[formId] = { ...newInitialData };

        // Check for changes after updating initial data
        checkForChanges();
      }
    },
    []
  );

  // Reset all forms to initial state
  const resetAllForms = useCallback(() => {
    console.log(`[UnsavedChanges] Resetting all forms`);
    Object.keys(formDataRef.current).forEach((formId) => {
      if (initialDataRef.current[formId]) {
        formDataRef.current[formId] = { ...initialDataRef.current[formId] };
      }
    });

    setHasUnsavedChanges(false);
  }, []);

  // Semantic comparison for flight arrays (avoids false positives from structural differences)
  const flightsSemanticallyEqual = useCallback(
    (a: unknown, b: unknown): boolean => {
      if (a === b) return true;
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      const isEmptyRow = (f: any) =>
        (f?.flight_number ?? "") === "" &&
        ((f?.company ?? f?.airline ?? "") as string) === "" &&
        (f?.date ?? "") === "" &&
        (f?.time ?? "") === "" &&
        (f?.airport ?? "") === "";
      const filterNonEmpty = (arr: any[]) =>
        arr.filter((f: any) => !isEmptyRow(f));
      const normA = filterNonEmpty(a);
      const normB = filterNonEmpty(b);
      if (normA.length !== normB.length) return false;
      const normDate = (d: any) =>
        formatDateForInput(d ?? "") || (d ?? "") || "";
      for (let i = 0; i < normA.length; i++) {
        const p = normA[i] as any;
        const q = normB[i] as any;
        const pCompany = (p?.company ?? p?.airline ?? "") as string;
        const qCompany = (q?.company ?? q?.airline ?? "") as string;
        if (
          (p?.direction ?? "arrival") !== (q?.direction ?? "arrival") ||
          (p?.flight_number ?? "") !== (q?.flight_number ?? "") ||
          pCompany !== qCompany ||
          normDate(p?.date) !== normDate(q?.date) ||
          (p?.time ?? "") !== (q?.time ?? "") ||
          (p?.airport ?? "") !== (q?.airport ?? "")
        )
          return false;
      }
      return true;
    },
    []
  );

  // Optimized object comparison function
  const compareObjects = useCallback(
    (obj1: unknown, obj2: unknown, keyHint?: string): boolean => {
      // Flight arrays use semantic comparison
      if (
        keyHint === "international_flights" ||
        keyHint === "domestic_flights"
      ) {
        return !flightsSemanticallyEqual(obj1, obj2);
      }

      // Quick reference check
      if (obj1 === obj2) return false;

      // Type check
      if (typeof obj1 !== typeof obj2) return true;

      // Null/undefined check
      if (obj1 == null || obj2 == null) return obj1 !== obj2;

      // Array check
      if (Array.isArray(obj1) !== Array.isArray(obj2)) return true;

      if (Array.isArray(obj1)) {
        if (!Array.isArray(obj2) || obj1.length !== obj2.length) return true;
        for (let i = 0; i < obj1.length; i++) {
          if (compareObjects(obj1[i], obj2[i])) return true;
        }
        return false;
      }

      // Object check
      if (typeof obj1 === "object") {
        const keys1 = Object.keys(obj1 as Record<string, unknown>);
        const keys2 = Object.keys(obj2 as Record<string, unknown>);

        if (keys1.length !== keys2.length) return true;

        for (const key of keys1) {
          if (!keys2.includes(key)) return true;
          if (
            compareObjects(
              (obj1 as Record<string, unknown>)[key],
              (obj2 as Record<string, unknown>)[key],
              key
            )
          )
            return true;
        }
        return false;
      }

      // Primitive comparison
      return obj1 !== obj2;
    },
    [flightsSemanticallyEqual]
  );

  // Check if any form has unsaved changes with performance optimization
  const checkForChanges = useCallback(() => {
    const enabledForms = Array.from(enabledFormsRef.current);

    if (enabledForms.length === 0) {
      console.log(
        `[UnsavedChanges] No enabled forms, setting hasUnsavedChanges to false`
      );
      setHasUnsavedChanges(false);
      return false;
    }

    let hasChanges = false;

    for (const formId of enabledForms) {
      const currentData = formDataRef.current[formId];
      const initialData = initialDataRef.current[formId];

      if (currentData && initialData) {
        const hasFormChanges = compareObjects(currentData, initialData);
        if (hasFormChanges) {
          hasChanges = true;
          console.log(`[UnsavedChanges] Form ${formId} has changes`);
          break; // Early exit for better performance
        }
      } else {
        console.log(`[UnsavedChanges] Form ${formId} missing data:`, {
          hasCurrentData: !!currentData,
          hasInitialData: !!initialData,
        });
      }
    }

    console.log(`[UnsavedChanges] Checking changes: ${hasChanges}`, {
      enabledForms,
      totalForms: Array.from(registeredFormsRef.current).length,
    });

    // Update state immediately
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  }, [compareObjects]);

  // Update baseline (initial) only - keeps current form data unchanged (for partial merges)
  const updateBaselineData = useCallback(
    (formId: string, newBaselineData: any) => {
      if (registeredFormsRef.current.has(formId)) {
        initialDataRef.current[formId] = { ...newBaselineData };
        checkForChanges();
      }
    },
    [checkForChanges]
  );

  // Get specific changes for a form
  const getFormChanges = useCallback(
    (formId: string) => {
      const currentData = formDataRef.current[formId];
      const initialData = initialDataRef.current[formId];
      const changes: { field: string; oldValue: any; newValue: any }[] = [];

      if (currentData && initialData) {
        Object.keys(currentData).forEach((key) => {
          const isFlightKey =
            key === "international_flights" || key === "domestic_flights";
          const hasChange = isFlightKey
            ? !flightsSemanticallyEqual(currentData[key], initialData[key])
            : JSON.stringify(currentData[key]) !==
              JSON.stringify(initialData[key]);

          if (hasChange) {
            changes.push({
              field: key,
              oldValue: initialData[key],
              newValue: currentData[key],
            });
          }
        });
      }

      return changes;
    },
    [flightsSemanticallyEqual]
  );

  // Get all changes across all forms
  const getAllChanges = useCallback(() => {
    const allChanges: Record<
      string,
      { field: string; oldValue: any; newValue: any }[]
    > = {};

    Array.from(registeredFormsRef.current).forEach((formId) => {
      if (enabledFormsRef.current.has(formId)) {
        allChanges[formId] = getFormChanges(formId);
      }
    });

    return allChanges;
  }, [getFormChanges]);

  // Role-based access control
  const isFormEnabled = useCallback((formId: string) => {
    return enabledFormsRef.current.has(formId);
  }, []);

  const setFormEnabled = useCallback(
    (formId: string, enabled: boolean) => {
      if (enabled) {
        enabledFormsRef.current.add(formId);
      } else {
        enabledFormsRef.current.delete(formId);
      }
      checkForChanges();
    },
    [checkForChanges]
  );

  // Section-specific operations
  const updateSectionData = useCallback(
    (formId: string, section: string, data: any) => {
      if (
        !registeredFormsRef.current.has(formId) ||
        !enabledFormsRef.current.has(formId)
      ) {
        return;
      }

      // Initialize section data if not exists
      if (!sectionDataRef.current[formId]) {
        sectionDataRef.current[formId] = {};
      }
      if (!initialSectionDataRef.current[formId]) {
        initialSectionDataRef.current[formId] = {};
      }

      // Update section data
      sectionDataRef.current[formId][section] = { ...data };

      // Update main form data
      const updatedFormData = { ...formDataRef.current[formId], ...data };
      formDataRef.current[formId] = updatedFormData;

      checkForChanges();
    },
    [checkForChanges]
  );

  const resetSectionData = useCallback(
    (formId: string, section: string) => {
      if (!registeredFormsRef.current.has(formId)) return;

      // Reset section data to initial
      if (initialSectionDataRef.current[formId]?.[section]) {
        sectionDataRef.current[formId][section] = {
          ...initialSectionDataRef.current[formId][section],
        };

        // Update main form data
        const updatedFormData = {
          ...formDataRef.current[formId],
          ...initialSectionDataRef.current[formId][section],
        };
        formDataRef.current[formId] = updatedFormData;

        checkForChanges();
      }
    },
    [checkForChanges]
  );

  const getSectionChanges = useCallback(
    (formId: string, section: string) => {
      const currentSectionData = sectionDataRef.current[formId]?.[section];
      const initialSectionData =
        initialSectionDataRef.current[formId]?.[section];
      const changes: { field: string; oldValue: any; newValue: any }[] = [];

      if (currentSectionData && initialSectionData) {
        // Get all unique keys from both objects
        const allKeys = new Set([
          ...Object.keys(currentSectionData),
          ...Object.keys(initialSectionData),
        ]);

        allKeys.forEach((key) => {
          const currentValue = currentSectionData[key];
          const initialValue = initialSectionData[key];

          if (compareObjects(currentValue, initialValue)) {
            changes.push({
              field: key,
              oldValue: initialValue,
              newValue: currentValue,
            });
          }
        });
      }

      return changes;
    },
    [compareObjects]
  );

  // Check if a specific section has changes
  const hasSectionChanges = useCallback(
    (formId: string, section: string) => {
      const changes = getSectionChanges(formId, section);
      return changes.length > 0;
    },
    [getSectionChanges]
  );

  // Get change count for a specific section
  const getSectionChangeCount = useCallback(
    (formId: string, section: string) => {
      const changes = getSectionChanges(formId, section);
      return changes.length;
    },
    [getSectionChanges]
  );

  // Get all section changes for a form
  const getAllSectionChanges = useCallback(
    (formId: string) => {
      const allSectionChanges: Record<
        string,
        { field: string; oldValue: any; newValue: any }[]
      > = {};

      if (!sectionDataRef.current[formId]) return allSectionChanges;

      Object.keys(sectionDataRef.current[formId]).forEach((section) => {
        allSectionChanges[section] = getSectionChanges(formId, section);
      });

      return allSectionChanges;
    },
    [getSectionChanges]
  );

  // Reset all sections for a form
  const resetAllSections = useCallback(
    (formId: string) => {
      if (!registeredFormsRef.current.has(formId)) return;

      if (
        sectionDataRef.current[formId] &&
        initialSectionDataRef.current[formId]
      ) {
        Object.keys(sectionDataRef.current[formId]).forEach((section) => {
          if (initialSectionDataRef.current[formId]?.[section]) {
            sectionDataRef.current[formId][section] = {
              ...initialSectionDataRef.current[formId][section],
            };
          }
        });

        // Update main form data with all reset section data
        const allResetData = Object.values(
          sectionDataRef.current[formId]
        ).reduce(
          (acc, sectionData) => ({
            ...acc,
            ...sectionData,
          }),
          {}
        );

        formDataRef.current[formId] = {
          ...formDataRef.current[formId],
          ...allResetData,
        };

        checkForChanges();
      }
    },
    [checkForChanges]
  );

  // Save section data
  const saveSectionData = useCallback(
    async (formId: string, section: string, data: any) => {
      try {
        if (!registeredFormsRef.current.has(formId)) {
          console.warn(
            `[UnsavedChanges] Cannot save section data for unregistered form: ${formId}`
          );
          return false;
        }

        if (!enabledFormsRef.current.has(formId)) {
          console.warn(
            `[UnsavedChanges] Cannot save section data for disabled form: ${formId}`
          );
          return false;
        }

        // Update section data
        if (!sectionDataRef.current[formId]) {
          sectionDataRef.current[formId] = {};
        }
        sectionDataRef.current[formId][section] = { ...data };

        // Update initial section data to mark as saved
        if (!initialSectionDataRef.current[formId]) {
          initialSectionDataRef.current[formId] = {};
        }
        initialSectionDataRef.current[formId][section] = { ...data };

        // Update main form data
        const updatedFormData = { ...formDataRef.current[formId], ...data };
        formDataRef.current[formId] = updatedFormData;

        // Update initial form data
        initialDataRef.current[formId] = { ...updatedFormData };

        checkForChanges();

        console.log(
          `[UnsavedChanges] Section ${section} data saved for form ${formId}`
        );
        return true;
      } catch (error) {
        console.error(
          `[UnsavedChanges] Failed to save section data for ${formId}/${section}:`,
          error
        );
        return false;
      }
    },
    [checkForChanges]
  );

  // Visual indicators
  const getUnsavedFieldsCount = useCallback(() => {
    let totalCount = 0;
    Array.from(registeredFormsRef.current).forEach((formId) => {
      if (enabledFormsRef.current.has(formId)) {
        totalCount += getFormChanges(formId).length;
      }
    });
    return totalCount;
  }, [getFormChanges]);

  const hasFormUnsavedChanges = useCallback(
    (formId: string) => {
      if (!enabledFormsRef.current.has(formId)) return false;
      return getFormChanges(formId).length > 0;
    },
    [getFormChanges]
  );

  // Data integrity
  const validateFormData = useCallback((formId: string) => {
    const validator = formValidationRef.current[formId];
    if (!validator) return true; // No validator means valid

    const formData = formDataRef.current[formId];
    return validator(formData);
  }, []);

  const saveFormData = useCallback(
    async (formId: string, data: any) => {
      const saveHandler = formSaveHandlersRef.current[formId];
      if (!saveHandler) {
        console.warn(`[UnsavedChanges] No save handler for form: ${formId}`);
        return false;
      }

      try {
        const success = await saveHandler(data);
        if (success) {
          // Update initial data after successful save
          initialDataRef.current[formId] = { ...data };
          formDataRef.current[formId] = { ...data };
          checkForChanges();
        }
        return success;
      } catch (error) {
        console.error(
          `[UnsavedChanges] Save failed for form ${formId}:`,
          error
        );
        return false;
      }
    },
    [checkForChanges]
  );

  const discardFormChanges = useCallback(
    (formId: string) => {
      if (!registeredFormsRef.current.has(formId)) return;

      // Reset to initial data
      if (initialDataRef.current[formId]) {
        formDataRef.current[formId] = { ...initialDataRef.current[formId] };
      }

      // Reset section data
      if (initialSectionDataRef.current[formId]) {
        sectionDataRef.current[formId] = {
          ...initialSectionDataRef.current[formId],
        };
      }

      checkForChanges();
    },
    [checkForChanges]
  );

  // Note: hasUnsavedChanges state is now updated directly in checkForChanges()

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle navigation attempts when there are unsaved changes
  const attemptNavigation = useCallback(
    (href: string, method: "push" | "replace") => {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
        setPendingNavigation("navigation");
        setPendingNavigationData({ href, method });
      }
    },
    [
      hasUnsavedChanges,
      setShowUnsavedDialog,
      setPendingNavigation,
      setPendingNavigationData,
    ]
  );

  const contextValue: UnsavedChangesContextType = {
    // Core state
    hasUnsavedChanges,
    setHasUnsavedChanges,

    // Form management
    registerForm,
    unregisterForm,
    updateFormData,
    resetFormData,
    resetAllForms,
    updateInitialData,
    updateBaselineData,

    // Change detection
    checkForChanges,
    getFormChanges,
    getAllChanges,

    // Navigation protection
    showUnsavedDialog,
    setShowUnsavedDialog,
    pendingNavigation,
    setPendingNavigation,
    pendingNavigationData,
    setPendingNavigationData,
    attemptNavigation,

    // Role-based access
    isFormEnabled,
    setFormEnabled,

    // Section-specific operations
    updateSectionData,
    resetSectionData,
    getSectionChanges,
    hasSectionChanges,
    getSectionChangeCount,
    getAllSectionChanges,
    resetAllSections,
    saveSectionData,

    // Visual indicators
    getUnsavedFieldsCount,
    hasFormUnsavedChanges,
    getChanges: getFormChanges,

    // Data integrity
    validateFormData,
    saveFormData,
    discardFormChanges,
  };

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider"
    );
  }
  return context;
}
